/**
 * postMessage bridge between the loader (host page) and the React
 * widget app (iframe). Both sides validate ``event.origin`` against
 * an allowlist to defend against same-page scripts impersonating
 * the other endpoint.
 */

export interface HostSuggestion {
  id?: string;
  label: string;
  prompt: string;
}

export interface HostPageContext {
  version?: number;
  key?: string;
  label?: string;
  title?: string;
  welcome?: string;
  summary?: string;
  tags?: string[];
  suggestions?: HostSuggestion[];
  page_id?: string;
  page_title?: string;
  page_type?: string;
  source_url?: string;
}

export interface HostOpenOptions {
  intent?: string;
  prompt?: string;
  label?: string;
  context?: HostPageContext | null;
}

export type HostMessage =
  | {
      type: "host:identity";
      href?: string;
      locale?: string;
      // Host page viewport at the time identity was sent. The iframe
      // can't read its own enclosing viewport reliably — ``innerWidth``
      // inside the iframe is the iframe's content width (PANEL_SIZE),
      // not the host's. We use these to decide mobile-fullscreen.
      hostWidth?: number;
      hostHeight?: number;
      // True only when the host loader just consumed a one-time
      // marker written before widget-initiated same-site navigation.
      // Manual refreshes/page loads do not set this, so the iframe
      // should not rehydrate the last server-side transcript.
      resumeAfterNavigation?: boolean;
    }
  | {
      // Sent by the loader on window resize / orientation change so
      // the iframe can react to host viewport changes after mount.
      type: "host:viewport";
      hostWidth: number;
      hostHeight: number;
    }
  | { type: "host:visibility"; open: boolean }
  | { type: "host:open"; options: HostOpenOptions }
  | { type: "host:close" }
  | { type: "host:context"; context: HostPageContext | null };

export type IframeMessage =
  | { type: "widget:resize"; width: number; height: number }
  | { type: "widget:visibility"; open: boolean }
  | { type: "widget:fullscreen"; on: boolean }
  | { type: "widget:navigate"; url: string; mode?: "tab" | "popup" | "host" }
  | { type: "widget:ready" };

const CONTEXT_STRING_LIMIT = 500;
const CONTEXT_WELCOME_LIMIT = 1000;
const CONTEXT_SUMMARY_LIMIT = 2000;
const CONTEXT_SUGGESTION_LIMIT = 8;

function boundedString(value: unknown, limit = CONTEXT_STRING_LIMIT): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().slice(0, limit);
  return normalized || undefined;
}

export function normalizeHostPageContext(value: unknown): HostPageContext | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const context: HostPageContext = {};
  if (typeof raw.version === "number" && Number.isFinite(raw.version)) {
    context.version = raw.version;
  }
  const stringFields: Array<keyof Pick<
    HostPageContext,
    "key" | "label" | "title" | "page_id" | "page_title" | "page_type" | "source_url"
  >> = ["key", "label", "title", "page_id", "page_title", "page_type", "source_url"];
  for (const field of stringFields) {
    const normalized = boundedString(raw[field]);
    if (normalized) context[field] = normalized;
  }
  const welcome = boundedString(raw.welcome, CONTEXT_WELCOME_LIMIT);
  if (welcome) context.welcome = welcome;
  const summary = boundedString(raw.summary, CONTEXT_SUMMARY_LIMIT);
  if (summary) context.summary = summary;
  if (Array.isArray(raw.tags)) {
    const tags = raw.tags
      .map((tag) => boundedString(tag, 80))
      .filter((tag): tag is string => Boolean(tag))
      .slice(0, 20);
    if (tags.length) context.tags = tags;
  }
  if (Array.isArray(raw.suggestions)) {
    const suggestions: HostSuggestion[] = [];
    for (const candidate of raw.suggestions.slice(0, CONTEXT_SUGGESTION_LIMIT)) {
      if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) continue;
      const row = candidate as Record<string, unknown>;
      const label = boundedString(row.label, 120);
      const prompt = boundedString(row.prompt, 500);
      if (!label || !prompt) continue;
      const id = boundedString(row.id, 120);
      suggestions.push(id ? { id, label, prompt } : { label, prompt });
    }
    if (suggestions.length) context.suggestions = suggestions;
  }
  return Object.keys(context).length ? context : null;
}

export function normalizeHostOpenOptions(value: unknown): HostOpenOptions {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const raw = value as Record<string, unknown>;
  const options: HostOpenOptions = {};
  const intent = boundedString(raw.intent, 120);
  const prompt = boundedString(raw.prompt, 1000);
  const label = boundedString(raw.label, 160);
  if (intent) options.intent = intent;
  if (prompt) options.prompt = prompt;
  if (label) options.label = label;
  if (Object.prototype.hasOwnProperty.call(raw, "context")) {
    options.context = normalizeHostPageContext(raw.context);
  }
  return options;
}

export function parseHostMessage(value: unknown): HostMessage | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  if (raw.type === "host:identity") {
    return {
      type: "host:identity",
      href: boundedString(raw.href, 2000),
      locale: boundedString(raw.locale, 40),
      hostWidth: finiteNumber(raw.hostWidth),
      hostHeight: finiteNumber(raw.hostHeight),
      resumeAfterNavigation: raw.resumeAfterNavigation === true,
    };
  }
  if (raw.type === "host:viewport") {
    const hostWidth = finiteNumber(raw.hostWidth);
    const hostHeight = finiteNumber(raw.hostHeight);
    if (hostWidth === undefined || hostHeight === undefined) return null;
    return { type: "host:viewport", hostWidth, hostHeight };
  }
  if (raw.type === "host:visibility" && typeof raw.open === "boolean") {
    return { type: "host:visibility", open: raw.open };
  }
  if (raw.type === "host:open") {
    return { type: "host:open", options: normalizeHostOpenOptions(raw.options) };
  }
  if (raw.type === "host:close") return { type: "host:close" };
  if (raw.type === "host:context") {
    return { type: "host:context", context: normalizeHostPageContext(raw.context) };
  }
  return null;
}

function finiteNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

/**
 * Send a message from the widget (iframe) to the host page.
 *
 * Posts to ``window.parent`` with target origin ``"*"`` because the
 * widget cannot know the host's origin reliably (and the host
 * validates ``event.source`` on receipt). The payload contains no
 * sensitive data — sizes, visibility, and a URL that the host will
 * sanity-check before opening — so a broadcast posture is safe.
 */
export function sendToHost(message: IframeMessage): void {
  if (typeof window === "undefined") return;
  if (window.parent === window) return; // not in an iframe
  window.parent.postMessage(message, "*");
}

/**
 * Subscribe to ``HostMessage`` events. Returns an unsubscribe
 * function.
 *
 * Host pages run on any origin (this is a customer-embeddable
 * widget), so we cannot restrict ``event.origin`` here. We do
 * validate ``event.source === window.parent`` so unrelated frames
 * on the page can't spoof host messages.
 */
export function listenToHost(
  handler: (message: HostMessage) => void,
): () => void {
  function onMessage(event: MessageEvent) {
    if (event.source !== window.parent) return;
    const message = parseHostMessage(event.data);
    if (!message) return;
    handler(message);
  }
  window.addEventListener("message", onMessage);
  return () => window.removeEventListener("message", onMessage);
}
